import type { VariantProps } from 'cva';

import { cva } from '@/ui/cva';

export const buttonVariants = cva({
    base: [
        'group/button inline-flex items-center justify-center gap-2xs rounded-full px-sm py-4 font-bold leading-none outline-none transition-all',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'hover:bg-gray hover:text-white hover:dark:bg-white hover:dark:text-gray',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue',
        'pressed:scale-95',
    ],
    variants: {
        color: {
            default: undefined,
            blue: undefined,
            green: undefined,
            red: undefined,
            gray: undefined,
            darkgray: undefined,
            orange: undefined,
            'red-to-purple': undefined,
            gradient: undefined,
            white: undefined,
        },
        variant: {
            filled: undefined,
            outlined: ['ring-1 ring-inset ring-current', 'hover:ring-0'],
        },
    },
    defaultVariants: {
        variant: 'filled',
    },
    compoundVariants: [
        {
            variant: 'filled',
            color: 'default',
            className: 'bg-gray-100 text-gray dark:bg-white dark:text-gray',
        },
        {
            color: 'blue',
            variant: 'filled',
            className: 'bg-blue text-white',
        },
        {
            color: 'blue',
            variant: 'outlined',
            className: 'text-blue',
        },
        {
            variant: 'filled',
            color: 'green',
            className: 'bg-green text-white',
        },
        {
            variant: 'outlined',
            color: 'green',
            className: 'text-green',
        },
        {
            variant: 'filled',
            color: 'red',
            className: 'bg-red text-white',
        },
        {
            variant: 'outlined',
            color: 'red',
            className: 'text-red',
        },
        {
            variant: 'outlined',
            color: 'gray',
            className: 'text-gray ring-gray-200',
        },
        {
            variant: 'filled',
            color: 'darkgray',
            className: 'bg-gray-800 text-white',
        },
        {
            variant: 'outlined',
            color: 'darkgray',
            className: 'text-gray-800',
        },
        {
            variant: 'filled',
            color: 'orange',
            className: 'bg-orange text-white',
        },
        {
            variant: 'outlined',
            color: 'orange',
            className: 'text-orange',
        },
        {
            variant: 'filled',
            color: 'red-to-purple',
            className: [
                'relative z-0 text-white',
                'before:absolute before:z-[-1] before:h-full before:w-full before:rounded-[inherit] before:bg-gradient-to-r before:from-red before:to-purple',
                'after:absolute after:z-[-1] after:h-full after:w-full after:rounded-[inherit] after:bg-gray after:opacity-0 after:transition dark:after:bg-white',
                'hover:after:opacity-100',
            ],
        },
        {
            variant: 'outlined',
            color: 'red-to-purple',
            className: [
                'relative z-0 ring-0',
                'bg-gradient-to-r from-red to-purple text-black',
                'before:absolute before:z-[-1] before:h-full before:w-full before:rounded-[inherit] before:border before:border-transparent before:bg-white before:bg-clip-padding',
                'after:absolute after:z-[-1] after:h-full after:w-full after:rounded-[inherit] after:bg-gray after:opacity-0 after:transition dark:after:bg-white',
                'hover:text-white',
                'hover:after:opacity-100',
            ],
        },
        {
            variant: 'filled',
            color: 'gradient',
            className: [
                'relative z-0 text-white',
                'before:absolute before:z-[-1] before:h-full before:w-full before:rounded-[inherit] before:bg-gradient-to-r before:from-[--gradient-start] before:to-[--gradient-end]',
                'after:absolute after:z-[-1] after:h-full after:w-full after:rounded-[inherit] after:bg-gray after:opacity-0 after:transition dark:after:bg-white',
                'hover:after:opacity-100',
            ],
        },
        {
            variant: 'outlined',
            color: 'gradient',
            className: [
                'relative z-0 ring-0',
                'bg-gradient-to-r from-[--gradient-start] to-[--gradient-end] text-black',
                'before:absolute before:z-[-1] before:h-full before:w-full before:rounded-[inherit] before:border before:border-transparent before:bg-white before:bg-clip-padding',
                'after:absolute after:z-[-1] after:h-full after:w-full after:rounded-[inherit] after:bg-gray after:opacity-0 after:transition dark:after:bg-white',
                'hover:text-white',
                'hover:after:opacity-100',
            ],
        },
        {
            variant: 'filled',
            color: 'white',
            className: 'bg-white text-gray',
        },
    ],
});

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
